// src/pages/LessonsHub.js
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import { listModules, getModule, getMe, getLessonProgress } from "../api/client";
import LessonsModuleView from "./LessonsModuleView";
import { useLanguage } from "../contexts/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";
import { useSubject } from "../contexts/SubjectContext";
import ModuleSidebar, {
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED,
} from "../components/ModuleSidebar";


export default function LessonsHub() {
  const { locale } = useLanguage();
  const { t } = useTranslation();
  const { subject } = useSubject();
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingModule, setLoadingModule] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [user, setUser] = useState(null);
  const [lessonProgress, setLessonProgress] = useState([]);
  
  const qModuleId = searchParams.get("module");
  const asideWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // NEU: Module mit Sprache laden
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Loading modules for language:', locale); // Debug
        const data = await listModules(); // Sprache übergeben
        const sorted = (Array.isArray(data) ? data : []).sort(
          (a, b) => (a.order_index || 0) - (b.order_index || 0)
        );
        setModules(sorted);
        if (!qModuleId && sorted.length) {
          setSearchParams({ module: sorted[0].id });
        }
      } catch (e) {
        console.error("listModules error:", e);
        setModules([]);
        setError(t.common.error); // ÜBERSETZT
      } finally {
        setLoading(false);
      }
    })();
  }, [locale, subject, t]);

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        if (me && me.id) {
          setUser(me);
          const lp = await getLessonProgress(me.id);
          setLessonProgress(Array.isArray(lp) ? lp : []);
        }
      } catch (e) {
        console.error("LessonsHub user/progress load error:", e);
        setLessonProgress([]);
      }
    })();
  }, []);

  // NEU: Aktives Modul mit Sprache laden
  useEffect(() => {
    if (!qModuleId) {
      setActiveModule(null);
      return;
    }
    (async () => {
      try {
        setLoadingModule(true);
        const mod = await getModule(qModuleId); // Sprache übergeben
        setActiveModule(mod || null);
      } catch (e) {
        console.error("getModule in hub error:", e);
        setActiveModule(null);
      } finally {
        setLoadingModule(false);
      }
    })();
  }, [qModuleId, locale]); // NEU: Lädt neu bei Sprachwechsel

  const handleSelectModule = (id) => {
    setSearchParams({ module: id });
  };

  if (loading) {
    return (
      <div 
        className="d-flex justify-content-center align-items-center" 
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">{t.common.loading}</span> {/* ÜBERSETZT */}
      </div>
    );
  }

  const currentModuleTitle = modules.find(m => m.id === qModuleId)?.title || t.modules.selectModule; // ÜBERSETZT

  return (
    <>
      {/* Desktop: Module Sidebar */}
      {isDesktop && (
        <ModuleSidebar
            modules={modules}
            activeModuleId={qModuleId}
            collapsed={collapsed}
            onToggleCollapsed={() => setCollapsed((v) => !v)}
            onSelectModule={handleSelectModule}
            leftOffset={240}
            zIndex={100}
            transitionMs={180}
          />
        )}

      {/* Main Content */}
      <main
        className="lessonshub-main"
          style={{
            background: "var(--bg-secondary)",
            minHeight: "100vh",
            transition: "padding-left 180ms ease",
            paddingLeft: isDesktop ? asideWidth : 0,
        }}
      >
        <div className="container py-4">
          {/* Mobile: Module Dropdown */}
          {!isDesktop && (
            <div className="mb-3">
              <Dropdown className="module-select-dropdown">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="w-100 text-start d-flex justify-content-between align-items-center"
                  style={{
                    height: "auto",
                    minHeight: "48px",
                    padding: "12px 16px",
                    fontSize: "1rem",
                    border: "1px solid var(--border-color)",
                    backgroundColor: "var(--bg-card)",
                    color: "var(--text-primary)"
                  }}
                >
                  <span style={{ 
                    flex: 1,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    paddingRight: "8px"
                  }}>
                    {currentModuleTitle}
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-100" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {modules.map((mod) => (
                    <Dropdown.Item
                      key={mod.id}
                      active={mod.id === qModuleId}
                      onClick={() => handleSelectModule(mod.id)}
                      style={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: "10px 16px"
                      }}
                    >
                      <div>
                        <div className="fw-semibold" style={{ lineHeight: "1.3" }}>
                          {mod.title}
                        </div>
                        <small className="text-muted d-block mt-1">
                          {mod.year_level}. {t.modules.class} • {t.modules.semester} {mod.semester} {/* ÜBERSETZT */}
                        </small>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}

          {!activeModule && !loadingModule && (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "calc(100vh - 120px)" }}>
              <Card className="text-center py-5 px-4" style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)", maxWidth: "400px" }}>
                <Card.Body>
                  <h4 className="mb-3" style={{ color: "var(--text-primary)" }}>{t.modules.selectModule}</h4> {/* ÜBERSETZT */}
                  <p className="mb-0" style={{ color: "var(--text-muted)" }}>
                    {isDesktop
                      ? t.modules.selectModuleDesktop // ÜBERSETZT
                      : t.modules.selectModuleMobile} {/* ÜBERSETZT */}
                  </p>
                </Card.Body>
              </Card>
            </div>
          )}

          {loadingModule && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">{t.common.loading}</p> {/* ÜBERSETZT */}
            </div>
          )}

          {error && (
            <Card className="border-danger">
              <Card.Body>
                <p className="text-danger mb-0">{error}</p>
                <small className="text-muted">{t.modules.loadError}</small> {/* ÜBERSETZT */}
              </Card.Body>
            </Card>
          )}

          {activeModule && !loadingModule && (
            <LessonsModuleView 
              moduleData={activeModule}
              lessonProgress={lessonProgress}
              userId={user?.id}
            />
          )}
        </div>
      </main>

      <style>{`
        .module-dropdown-mobile .dropdown-toggle {
          height: auto !important;
          min-height: 38px;
          white-space: normal !important;
          text-align: left;
        }

        .module-dropdown-mobile .dropdown-menu {
          overflow-x: hidden !important;
        }
      `}</style>
    </>
  );
}
